import type { FetchService } from '../services/browser/FetchService';
import type { TimerService } from '../services/browser/TimerService';
import type { TrackingDebugService } from '../services/TrackingDebugService';
import type { HeartbeatPayload } from '../PayloadBuilder';

interface ApiClientOptions {
  apiUrl: string;
  /**
   * Full storefront domain URL for this page (e.g. `https://shop.example/en-de`),
   * sent to `/tracking/initialize` as `?domain=` so the server can resolve hosts
   * that front several projects by path prefix. Omitted → server falls back to the
   * `Origin` header. A query param (not a header) keeps this a CORS-simple GET.
   */
  storefrontUrl?: string;
}

interface ApiClientCallbacks {
  /** Fired when `/tracking/initialize` returns any-null in `{ url, token, ttl }` — tracking is off for this project. */
  onDeactivated: () => void;
}

interface InitializeResponse {
  url: string | null;
  token: string | null;
  ttl: number | null;
}

/** Refresh `ttl - REFRESH_LEAD_MS` ms before expiry, so the next heartbeat already has a fresh token. */
const REFRESH_LEAD_MS = 60_000;
/** Floor when `ttl - REFRESH_LEAD_MS` would be non-positive (very short TTL). */
const REFRESH_FLOOR_MS = 5_000;
/** Backoff schedule for transient network failures on the periodic POST path. */
const NETWORK_RETRY_BACKOFFS_MS: readonly number[] = [250, 1000];

/**
 * Talks to the Birdsview `/tracking/*` API.
 *
 * - `initialize` fetches `{ url, token, ttl }`. Token is held in memory and replayed as
 *   `Authorization: Bearer <token>` on every collect POST. A refresh timer fires shortly
 *   before TTL so we never see a 401 in the steady state.
 * - `collect(payload)` sends one heartbeat per POST. On `401` we re-initialize once and
 *   retry the single payload; on `400` and other non-204 we drop. Network rejects retry
 *   with a tiny backoff schedule, then drop — heartbeats are time-sensitive.
 * - `collect(payload, { final: true })` is the unload path: a single keepalive POST,
 *   fire-and-forget, no beacon (beacon cannot carry custom headers).
 *
 * Deactivate signal: when `/tracking/initialize` returns any null in `{ url, token, ttl }`
 * we invoke `onDeactivated` so the surrounding `TrackingClient` can tear the loop down.
 */
export class ApiClient {
  private collectUrl: string | null = null;
  private token: string | null = null;
  private refreshTimerId: number | null = null;

  constructor(
    private readonly options: ApiClientOptions,
    private readonly fetcher: FetchService,
    private readonly timers: TimerService,
    private readonly callbacks: ApiClientCallbacks,
    private readonly debug?: TrackingDebugService,
  ) {}

  async initialize(): Promise<boolean> {
    const requestUrl = new URL(this.options.apiUrl.replace(/\/+$/, '') + '/tracking/initialize');
    if (this.options.storefrontUrl) {
      requestUrl.searchParams.set('domain', this.options.storefrontUrl);
    }
    const url = requestUrl.toString();

    let res: Response;
    try {
      res = await this.fetcher.fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
    } catch {
      this.debug?.log('ApiClient', 'initialize: network error', {});
      this.clearAuth();

      return false;
    }

    if (!res.ok) {
      this.debug?.log('ApiClient', 'initialize: HTTP error', { status: res.status });
      this.clearAuth();

      return false;
    }

    let body: InitializeResponse;
    try {
      body = (await res.json()) as InitializeResponse;
    } catch {
      this.debug?.log('ApiClient', 'initialize: unparseable JSON', {});
      this.clearAuth();

      return false;
    }

    if (body.url == null || body.token == null || body.ttl == null) {
      this.debug?.log('ApiClient', 'initialize: deactivated', {});
      this.cancelRefreshTimer();
      this.clearAuth();
      this.callbacks.onDeactivated();

      return false;
    }

    // Resolve `body.url` against the URL the server actually answered from (`res.url`).
    // Absolute body URLs pass through; relative ones (e.g. "/tracking/collect") become
    // absolute against the API host instead of the storefront page. Fallback to the
    // request URL when `res.url` is empty (test doubles, some non-browser fetch impls).
    this.collectUrl = new URL(body.url, res.url || url).toString();
    this.token = body.token;
    this.scheduleRefresh(body.ttl);
    this.debug?.log('ApiClient', 'initialize: ok', { ttl: body.ttl });

    return true;
  }

  collect(
    payload: HeartbeatPayload,
    options?: { final?: boolean }
  ): void {
    if (!this.collectUrl || !this.token) {
      this.debug?.log('ApiClient', 'collect skipped: not initialized', { final: !!options?.final });

      return;
    }

    if (options?.final) {
      void this.sendFinal(payload);

      return;
    }

    void this.sendPeriodic(payload, 0);
  }

  dispose(): void {
    this.debug?.log('ApiClient', 'dispose', {});
    this.cancelRefreshTimer();
    this.clearAuth();
  }

  private async sendPeriodic(
    payload: HeartbeatPayload,
    attempt: number
  ): Promise<void> {
    if (!this.collectUrl || !this.token) {
      return;
    }
    const url = this.collectUrl;
    const token = this.token;

    let res: Response;
    try {
      res = await this.fetcher.fetch(url, {
        method: 'POST',
        headers: this.postHeaders(token),
        body: JSON.stringify(payload),
      });
    } catch {
      const backoff = NETWORK_RETRY_BACKOFFS_MS[attempt];
      if (backoff == null) {
        this.debug?.log('ApiClient', 'collect dropped: network retries exhausted', { attempts: attempt + 1 });

        return;
      }
      this.timers.setTimeout(() => {
        void this.sendPeriodic(payload, attempt + 1);
      }, backoff);

      return;
    }

    if (res.status === 204) {
      return;
    }

    if (res.status === 401) {
      this.debug?.log('ApiClient', 'collect 401: re-initializing', {});
      const ok = await this.initialize();
      if (!ok || !this.collectUrl || !this.token) {
        this.debug?.log('ApiClient', 'collect 401: re-init unavailable, dropping', {});

        return;
      }
      try {
        const retryRes = await this.fetcher.fetch(this.collectUrl, {
          method: 'POST',
          headers: this.postHeaders(this.token),
          body: JSON.stringify(payload),
        });
        if (retryRes.status !== 204) {
          this.debug?.log('ApiClient', 'collect 401 retry: dropped', { status: retryRes.status });
        }
      } catch {
        this.debug?.log('ApiClient', 'collect 401 retry: network error, dropping', {});
      }

      return;
    }

    this.debug?.log('ApiClient', 'collect dropped', { status: res.status });
  }

  private async sendFinal(payload: HeartbeatPayload): Promise<void> {
    if (!this.collectUrl || !this.token) {
      return;
    }
    try {
      await this.fetcher.fetchKeepAlive(this.collectUrl, {
        method: 'POST',
        headers: this.postHeaders(this.token),
        body: JSON.stringify(payload),
      });
    } catch {
      // Unload path: fire-and-forget.
    }
  }

  private scheduleRefresh(ttlSeconds: number): void {
    this.cancelRefreshTimer();
    const delayMs = Math.max(ttlSeconds * 1000 - REFRESH_LEAD_MS, REFRESH_FLOOR_MS);
    this.refreshTimerId = this.timers.setTimeout(() => {
      this.refreshTimerId = null;
      void this.initialize();
    }, delayMs);
  }

  private cancelRefreshTimer(): void {
    if (this.refreshTimerId == null) {
      return;
    }
    this.timers.clearTimeout(this.refreshTimerId);
    this.refreshTimerId = null;
  }

  private clearAuth(): void {
    this.collectUrl = null;
    this.token = null;
  }

  private postHeaders(token: string): Record<string, string> {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }
}
